import React, { useEffect, useState, useCallback, useMemo } from "react";
import { Alert, Box, Button, Stack, Text, Title, Tooltip } from "@mantine/core";
import {
  IconChevronLeft,
  IconLock,
  IconPencil,
  IconX,
} from "@tabler/icons-react";
import moment from "moment";
import BrandedLoadingOverlay from "../../common/BrandedLoadingOverlay";
import { useApp } from "../../../context/AppContext";
import { CashTenderDetails } from "../../../types/cashDrawer";
import PaymentSuccessMessage from "../PaymentSuccessMessage";
import PaymentProcessingModal from "../PaymentProcessingModal";
import AddCardModal from "../../common/AddCardModal/AddCardModal";
import TipModal from "../../calendar/CalendarView/components/TransactionDetailModal/TipModal";
import { formatCurrency } from "../../../utils/cashDrawerUtils";
import {
  GetTransactionsResponse,
  TransactionResponse,
  EditTransactionLineItemsRequest,
} from "../../../types/transactions";
import {
  ProcessCardPaymentResponse,
  ProcessNonCardPaymentResponse,
} from "../../../types/payments";
import {
  ChargeCardOnFileResponse,
  ClientPaymentMethod,
  isCardExpired,
} from "../../../types/cardOnFile";
import { useSalon } from "../../../context/SalonContext";
import { BusinessInfo, SalonContextType } from "../../../types/context";
import {
  ClientServicePackage,
  RedeemPackageResponse,
} from "../../../types/packages";
import { SalonPaymentMethod } from "../../../types/salonPaymentMethods";
import {
  BnplMethodConfig,
  bnplMethodForSelection,
  isBnplSelection,
} from "../../../config/bnpl";
import {
  buildCoveredLines,
  hasRemainingBalance,
  sharedBalanceAwareMax,
} from "../../../utils/packageRedemptionUtils";
import { computeTransactionTotals } from "../../../utils/transactionTotals";
import { isTransactionInFinalState } from "../../../utils/transactionUtils";
import { RedeemLoyaltyRewardResponse } from "../../../types/loyalty";
import { LoyaltyRewardPick } from "../../rewards/LoyaltyRewardPickerModal";
import { CheckoutState, CheckoutStateActions } from "./useCheckoutState";
import PaymentMethodPanel from "./PaymentMethodPanel";
import {
  getPreferredTerminalDeviceId,
  setPreferredTerminalDeviceId,
} from "../../../utils/terminalPreference";
import CheckoutSummaryPanel from "./CheckoutSummaryPanel";
import ChargeAmountModal from "./ChargeAmountModal";
import CashOverageModal from "./CashOverageModal";
import FullscreenShell from "../../common/FullscreenShell/FullscreenShell";
import SurfacePaper from "../../common/surface/SurfacePaper";
import surfaceStyles from "../../common/surface/surface.module.css";
import { ConnectedCheckoutReminderAlert } from "../../common/CheckoutReminderAlert";
import styles from "./CheckoutPanel.module.css";

export interface CheckoutPanelProps {
  transaction: GetTransactionsResponse | TransactionResponse;
  remainingBalance: number;
  businessInfo: BusinessInfo;
  state: CheckoutState;
  actions: CheckoutStateActions;
  onTransactionUpdated: (
    updatedTransaction: GetTransactionsResponse | TransactionResponse,
  ) => void;
  onRefetchTransaction?: () => Promise<void>;
  /** Back chevron (top-left): return to details preserving selections. */
  onBack: () => void;
  /** Red "Cancel Checkout": host resets checkout state and returns. */
  onCancelCheckout: () => void;
  /** X (top-right): close the whole modal. */
  onCloseModal: () => void;
  /** Called after a successful payment settles (post success screen). */
  onPaymentComplete: () => void;
  /** Package-sale passthrough (QuickSale 100%-upfront flow). */
  amountDue?: number;
  amountNote?: string;
  packageSaleName?: string;
  /** Loyalty picks staged from Appointment Details' Rewards & Promotions
   *  section. CheckoutPanel commits the sum as one `{points}` redeem right
   *  before the money charge. Absent = feature not wired in this host. */
  stagedRewards?: LoyaltyRewardPick[];
  /** Fired after the redemption commits so the parent can clear the
   *  staging (post-commit, further picks against a new sale would need to
   *  be reopened via the picker). */
  onStagedRewardsChange?: (picks: LoyaltyRewardPick[]) => void;
}

/**
 * The redesigned checkout screen: FullscreenShell chrome with the
 * Payment Method paper (left) and Transaction Summary paper + actions
 * (right). Owns every charge flow ported from the legacy CheckoutModal —
 * terminal card, BNPL QR, inline card-on-file, cash tender, off-platform
 * record, package redemption — plus the per-unit quantity gate and the
 * charge-amount override.
 */
export default function CheckoutPanel({
  transaction,
  remainingBalance,
  businessInfo,
  state,
  actions,
  onTransactionUpdated,
  onRefetchTransaction,
  onBack,
  onCancelCheckout,
  onCloseModal,
  onPaymentComplete,
  amountDue,
  amountNote,
  packageSaleName,
  stagedRewards,
  onStagedRewardsChange,
}: CheckoutPanelProps) {
  const {
    businessInfo: salonBusinessInfo,
    services: salonServices,
    paymentTerminals,
  } = useSalon() as SalonContextType;
  const salonId = (salonBusinessInfo as BusinessInfo | null)?.id ?? "";
  const {
    createPaymentIntent,
    processNonCardPayment,
    getClientPaymentMethods,
    getClientServicePackages,
    getSalonPaymentMethods,
    getTransaction,
    editTransactionLineItems,
    chargeCardOnFile,
    redeemPackage,
    getClientGiftCards,
    redeemGiftCard,
    redeemLoyaltyReward,
  } = useApp();

  const [isProcessing, setIsProcessing] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showCashOverageModal, setShowCashOverageModal] = useState(false);
  /** When true, `PaymentProcessingModal` runs the card-on-file workflow (poll only). */
  const [paymentProcessingCardOnFile, setPaymentProcessingCardOnFile] =
    useState(false);
  const [paymentIntentId, setPaymentIntentId] = useState<string | null>(null);
  const [eligibleAmountForTip, setEligibleAmountForTip] = useState<
    number | null
  >(null);
  /** Which terminal flow `PaymentProcessingModal` runs: card tap vs BNPL QR. */
  const [terminalPaymentMethod, setTerminalPaymentMethod] = useState<
    "card" | "affirm"
  >("card");
  const [error, setError] = useState<string | null>(null);
  const [clientPaymentMethods, setClientPaymentMethods] = useState<
    ClientPaymentMethod[] | null
  >(null);
  const [clientPackages, setClientPackages] = useState<ClientServicePackage[]>(
    [],
  );
  // The client's total spendable gift-card balance (sum of active cards).
  const [clientGiftCardBalance, setClientGiftCardBalance] = useState(0);
  const [offPlatformMethods, setOffPlatformMethods] = useState<
    SalonPaymentMethod[]
  >([]);
  const [showAddCardModal, setShowAddCardModal] = useState(false);
  const [showAmountModal, setShowAmountModal] = useState(false);
  const [showTipModal, setShowTipModal] = useState(false);

  // Loyalty staging is owned by the Rewards & Promotions section on
  // Appointment Details and lifted through TransactionDetailModal; the
  // panel here simply reads the picks + commits them as one flat-rate
  // `{points}` redeem BEFORE the money charge fires (a failed commit
  // aborts Charge and preserves the picks so staff can retry).
  const stagedLoyaltyDollars = useMemo(
    () =>
      (stagedRewards ?? []).reduce(
        (acc, pick) =>
          acc + pick.quantity * (Number(pick.reward.applied_value) || 0),
        0,
      ),
    [stagedRewards],
  );
  const stagedLoyaltyPoints = useMemo(
    () =>
      (stagedRewards ?? []).reduce(
        (acc, pick) =>
          acc + pick.quantity * (Number(pick.reward.points_cost) || 0),
        0,
      ),
    [stagedRewards],
  );
  // Once a Loyalty tender is on the transaction, further stage commits
  // short-circuit — the ledger has already been drawn down.
  const loyaltyAlreadyRedeemed = useMemo(
    () =>
      (transaction.payments ?? []).some(
        (payment) =>
          payment.status === "succeeded" &&
          (payment.type === "loyalty_redemption" ||
            payment.method === "Loyalty"),
      ),
    [transaction.payments],
  );

  // Every method fetch resolves behind one loading gate so all pills —
  // preferred (Card Present / Card on File / Package) and off-platform
  // (Cash / salon methods / BNPL) — appear together instead of popping in
  // as each fetch lands. The terminal device details are prefetched here
  // too, so the Card Present reveal opens with the real device name.
  const [isLoadingCards, setIsLoadingCards] = useState(true);
  const [isLoadingPackages, setIsLoadingPackages] = useState(true);
  const [isLoadingGiftCards, setIsLoadingGiftCards] = useState(true);
  const [isLoadingOffPlatform, setIsLoadingOffPlatform] = useState(true);
  const isLoadingMethods =
    isLoadingCards ||
    isLoadingPackages ||
    isLoadingGiftCards ||
    isLoadingOffPlatform;

  const totals = computeTransactionTotals(transaction);
  // Charge amount MUST subtract the staged loyalty redemption on BOTH branches
  // (auto-computed and staff-override). The redeem POST at Charge time
  // (commitStagedRewards) writes a `Loyalty` tender for stagedLoyaltyDollars;
  // sending the full overrideAmount to Stripe on top of that double-books —
  // the sale collects (Stripe amount + Loyalty tender) which exceeds the
  // transaction total by exactly stagedLoyaltyDollars. The auto branch bug
  // shipped ~2 reviews ago and silently over-charged every appointment that
  // combined a Loyalty tender with a card/cash payment; the override branch
  // has the same shape and needs the same netting. Historical rows need a
  // data fix separately from this code fix.
  const chargeAmount = Math.max(
    0,
    (state.overrideAmount ?? remainingBalance) - stagedLoyaltyDollars,
  );
  // Gift card tender always applies the capped min (no amount override) — the
  // backend draws soonest-expiry-first and caps at the available balance. It is
  // a SEPARATE tender tile (its own Redeem action, redeemGiftCard), applied one
  // payment at a time, so it does NOT net out already-committed tenders — each
  // of those already reduced remainingBalance on refetch. It MUST, however, net
  // the STAGED (uncommitted) Loyalty redemption for the SAME reason chargeAmount
  // does: handleChargeClick commits the staged Loyalty tender
  // (commitStagedRewards) and then reads this value in the same tick, before the
  // parent's refetched remainingBalance re-renders — so an un-netted
  // min(balance, remainingBalance) would over-draw the card by exactly
  // stagedLoyaltyDollars.
  const giftCardAmountToApply = Math.min(
    clientGiftCardBalance,
    Math.max(0, remainingBalance - stagedLoyaltyDollars),
  );

  // Card on File is offered whenever the salon can charge saved cards
  // (Stripe Connect onboarded) — with zero cards the reveal shows the
  // dashed empty state with "Add card", matching the design.
  const connectOnboarded = !!(
    businessInfo?.stripe_connect_account_id &&
    businessInfo?.stripe_connect_account_is_onboarded
  );
  const showCardOnFile = connectOnboarded && !!transaction?.client?.id;

  // ── Data fetches ────────────────────────────────────────────────

  const checkForPackages = useCallback(async () => {
    if (!transaction?.client?.id || !salonId) {
      setClientPackages([]);
      setIsLoadingPackages(false);
      return;
    }
    setIsLoadingPackages(true);
    try {
      const list = await getClientServicePackages(
        salonId,
        transaction.client.id,
      );
      setClientPackages(list);
    } catch (err) {
      console.error("Error checking for client packages:", err);
      setClientPackages([]);
    } finally {
      setIsLoadingPackages(false);
    }
  }, [transaction?.client?.id, salonId, getClientServicePackages]);

  const checkForGiftCards = useCallback(async () => {
    if (!transaction?.client?.id || !salonId) {
      setClientGiftCardBalance(0);
      setIsLoadingGiftCards(false);
      return;
    }
    setIsLoadingGiftCards(true);
    try {
      const { held } = await getClientGiftCards(salonId, transaction.client.id);
      const spendable = held
        .filter((c) => c.status === "active" && c.balance > 0)
        .reduce((sum, c) => sum + c.balance, 0);
      setClientGiftCardBalance(spendable);
    } catch (err) {
      console.error("Error checking for client gift cards:", err);
      setClientGiftCardBalance(0);
    } finally {
      setIsLoadingGiftCards(false);
    }
  }, [transaction?.client?.id, salonId, getClientGiftCards]);

  const checkForCardsOnFile = useCallback(async () => {
    if (
      !transaction?.client?.id ||
      !businessInfo?.stripe_connect_account_id ||
      !businessInfo?.stripe_connect_account_is_onboarded
    ) {
      setClientPaymentMethods(null);
      setIsLoadingCards(false);
      return;
    }

    setIsLoadingCards(true);
    try {
      const response = await getClientPaymentMethods(transaction.client.id);
      const methods = response.paymentMethods || [];
      setClientPaymentMethods(methods);
    } catch (err) {
      console.error("Error checking for cards on file:", err);
      setClientPaymentMethods(null);
    } finally {
      setIsLoadingCards(false);
    }
  }, [
    transaction?.client?.id,
    getClientPaymentMethods,
    businessInfo?.stripe_connect_account_id,
    businessInfo?.stripe_connect_account_is_onboarded,
  ]);

  useEffect(() => {
    checkForCardsOnFile();
    checkForPackages();
    checkForGiftCards();
  }, [checkForCardsOnFile, checkForPackages, checkForGiftCards]);

  // Off-platform methods (Zelle/Venmo/CashApp/Check + custom).
  useEffect(() => {
    const id = businessInfo?.id;
    if (!id) {
      setIsLoadingOffPlatform(false);
      return;
    }
    let cancelled = false;
    setIsLoadingOffPlatform(true);
    (async () => {
      try {
        const list = await getSalonPaymentMethods(id);
        if (!cancelled) setOffPlatformMethods(list ?? []);
      } catch (err) {
        console.error(
          "Failed to load off-platform payment methods for checkout",
          err,
        );
      } finally {
        if (!cancelled) setIsLoadingOffPlatform(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [businessInfo?.id, getSalonPaymentMethods]);

  // Resolve which terminal to charge on. Auto-select the only terminal; with
  // several, default to this device's last-used one (localStorage) and
  // otherwise leave it for the staff to pick. Clear a stale pick if the chosen
  // terminal was removed.
  useEffect(() => {
    const terminals = paymentTerminals ?? [];
    if (terminals.length === 0) return;
    const ids = terminals.map((t) => t.stripe_terminal_device_id);
    const selected = state.selectedTerminalDeviceId;
    if (selected) {
      if (!ids.includes(selected)) actions.setSelectedTerminalDeviceId(null);
      return;
    }
    if (terminals.length === 1) {
      actions.setSelectedTerminalDeviceId(ids[0]);
      return;
    }
    const stored = getPreferredTerminalDeviceId(businessInfo?.id ?? "");
    if (stored && ids.includes(stored)) {
      actions.setSelectedTerminalDeviceId(stored);
    }
  }, [
    paymentTerminals,
    state.selectedTerminalDeviceId,
    businessInfo?.id,
    actions,
  ]);

  const selectedOffPlatform = useMemo(
    () =>
      offPlatformMethods
        .filter((m) => m.is_selected)
        .sort((a, b) => a.sort_order - b.sort_order),
    [offPlatformMethods],
  );

  // Auto-select the default (or first valid) saved card once cards load.
  useEffect(() => {
    if (!clientPaymentMethods || clientPaymentMethods.length === 0) return;
    const stillExists = clientPaymentMethods.some(
      (card) => card.id === state.selectedCardId,
    );
    if (state.selectedCardId !== null && stillExists) return;
    const candidates = clientPaymentMethods.filter(
      (card) => !isCardExpired(card),
    );
    const defaultCard =
      candidates.find((card) => card.isDefault) ?? candidates[0];
    if (defaultCard) {
      actions.setSelectedCardId(defaultCard.id);
    }
  }, [clientPaymentMethods, state.selectedCardId, actions]);

  // ── Helpers ported from CheckoutModal ───────────────────────────

  const formatDateTime = (dateTime: string) => {
    if (!dateTime) return "Unknown date/time";
    const date = moment.utc(dateTime);
    const startEST = date.tz(businessInfo?.timezone || "America/New_York");
    return startEST.format("MMM D, YYYY h:mm A");
  };

  const generatePaymentDescription = () => {
    const salonName = (businessInfo as any)?.salon_name || "Salon";
    const services = transaction.line_items.booked_services;
    // Package-sale transactions have neither services nor products, so default
    // to an empty list — products.map() would otherwise throw on the else path.
    const products = transaction.line_items.products ?? [];

    // A package/special sale: name what was bought so the Stripe description
    // (dashboard + receipt) is clear, instead of an empty product list.
    if (packageSaleName) {
      return `[${salonName}] Payment for ${packageSaleName}`;
    }

    if (services && services.length > 0) {
      let serviceText = services[0]?.name || "Service";
      if (services.length > 1) {
        serviceText += ` (+ ${services.length - 1} more)`;
      }

      const staffNames = services
        .map((service) => service.staff_name)
        .filter((name, index, arr) => arr.indexOf(name) === index);
      let staffText = staffNames[0] || "Staff";
      if (staffNames.length > 1) {
        staffText += ` (+ ${staffNames.length - 1} other)`;
      }

      const dateTime = formatDateTime(services[0]?.start_dt);

      return `[${salonName}] Payment for ${serviceText} with ${staffText} on ${dateTime}`;
    } else {
      const productsText = products.map((product) => product.name).join(", ");
      return `[${salonName}] Payment for ${productsText}`;
    }
  };

  const mapUpdatedTransactionAndPayments = (
    response: ProcessNonCardPaymentResponse | ProcessCardPaymentResponse,
  ) => {
    return {
      ...transaction,
      transaction: {
        ...transaction.transaction,
        ...response.transaction,
      },
      payments: response.payments,
    };
  };

  const settleWithSuccess = () => {
    setShowSuccess(true);
    setTimeout(() => {
      onPaymentComplete();
    }, 3000);
  };

  /**
   * Shared terminal handoff for in-person card and BNPL (QR-on-reader)
   * payments. Charges `chargeAmount` (the override, when set, else the
   * remaining balance).
   */
  const handleTerminalPayment = async (bnplMethod?: BnplMethodConfig) => {
    const terminalDeviceId = state.selectedTerminalDeviceId;
    if (!terminalDeviceId) {
      setError("Please select a terminal to charge on.");
      return;
    }
    // Remember this terminal on THIS device for next time.
    setPreferredTerminalDeviceId(businessInfo?.id ?? "", terminalDeviceId);
    setIsProcessing(true);
    setError(null);

    try {
      const response = await createPaymentIntent({
        amount: chargeAmount,
        transactionId: transaction.transaction.id,
        salonId: businessInfo.id,
        accountId: businessInfo.stripe_connect_account_id,
        description: generatePaymentDescription(),
        bookedServices: transaction.line_items.booked_services,
        type: "checkout",
        isPackageSale: !!packageSaleName,
        ...(bnplMethod
          ? { paymentMethodType: bnplMethod.paymentMethodType }
          : {}),
      });

      if (response?.paymentIntentId) {
        setPaymentProcessingCardOnFile(false);
        setTerminalPaymentMethod(bnplMethod ? bnplMethod.key : "card");
        setPaymentIntentId(response.paymentIntentId);
        // Normalize null/undefined to 0: PaymentProcessingModal's
        // initiatePayment guard early-returns on null, which would leave
        // the reader without a handoff (no QR / no card prompt).
        setEligibleAmountForTip(response.eligibleAmountForTip ?? 0);
        setShowPaymentModal(true);
      } else {
        setError("Failed to create payment intent");
      }
    } catch (err) {
      console.error("Error processing terminal payment:", err);
      setError(
        bnplMethod
          ? `Failed to start ${bnplMethod.label} payment`
          : "Failed to process card payment",
      );
    } finally {
      setIsProcessing(false);
    }
  };

  const handleNonCardPayment = async (
    cashDetails?: CashTenderDetails | null,
  ) => {
    if (
      !state.payMethod ||
      state.payMethod === "Card" ||
      state.payMethod === "CardOnFile" ||
      state.payMethod === "Package" ||
      state.payMethod === "GiftCard" ||
      isBnplSelection(state.payMethod)
    ) {
      return;
    }

    setIsProcessing(true);
    setError(null);

    try {
      // Cash may be a partial payment — the bill portion is capped at the
      // balance due; any tip the customer left is added on top so the
      // payment row reflects the full total handed to the salon (matches
      // the Stripe webhook convention where `amount` includes tip).
      const paymentAmount =
        state.payMethod === "Cash" &&
        cashDetails &&
        typeof cashDetails.amount === "number" &&
        cashDetails.amount > 0
          ? cashDetails.amount + (cashDetails.tip_amount || 0)
          : chargeAmount;

      const response: ProcessNonCardPaymentResponse =
        await processNonCardPayment({
          transaction_id: transaction.transaction.id,
          amount: paymentAmount,
          payment_method: state.payMethod,
          ...(state.payMethod === "Cash" && cashDetails
            ? { cash_details: cashDetails }
            : {}),
        });

      if (response) {
        const updatedTransaction = mapUpdatedTransactionAndPayments(response);
        onTransactionUpdated(updatedTransaction);
        settleWithSuccess();
      } else {
        setError("Failed to record payment");
      }
    } catch (err) {
      console.error("Error processing non-card payment:", err);
      setError("Failed to record payment");
    } finally {
      setIsProcessing(false);
    }
  };

  /**
   * Card-on-file charge may return a minimal payload. Refetch, then open
   * `PaymentProcessingModal` with `cardOnFile` (poll-only workflow; no reader).
   */
  const handleCardOnFileSuccess = async (
    response: ChargeCardOnFileResponse,
  ) => {
    setError(null);
    const intentId = response?.paymentIntentId || null;

    const applyMergedResponse = () => {
      if (response.transaction && response.payment) {
        const updatedTransaction = {
          ...transaction,
          transaction: {
            ...transaction.transaction,
            ...response.transaction,
          },
          payments: [...(transaction.payments || []), response.payment],
        };
        onTransactionUpdated(updatedTransaction);
        return true;
      }
      return false;
    };

    try {
      if (onRefetchTransaction) {
        await onRefetchTransaction();
      } else {
        const refreshed = await getTransaction(transaction.transaction.id);
        if (refreshed) {
          onTransactionUpdated(refreshed);
        } else if (!applyMergedResponse()) {
          setError(
            "Could not load updated transaction. If payment succeeded, close and reopen this appointment.",
          );
          return;
        }
      }
    } catch (err) {
      console.error(
        "Error refreshing transaction after card on file charge:",
        err,
      );
      if (!applyMergedResponse()) {
        setError(
          "Payment may have succeeded but the screen could not refresh. Close and reopen this appointment.",
        );
        return;
      }
    }

    if (intentId) {
      setPaymentIntentId(intentId);
      setEligibleAmountForTip(0);
      setPaymentProcessingCardOnFile(true);
      setShowPaymentModal(true);
    } else {
      settleWithSuccess();
    }
  };

  /** Inline card-on-file charge for the selected saved card. */
  const handleChargeCardOnFile = async () => {
    if (state.selectedCardId === null) return;
    setIsProcessing(true);
    setError(null);
    try {
      const response = await chargeCardOnFile({
        transactionId: transaction.transaction.id,
        paymentMethodId: state.selectedCardId,
        amountCents: Math.round(chargeAmount * 100),
      });
      await handleCardOnFileSuccess(response);
    } catch (err) {
      console.error("Error charging card on file:", err);
      setError(
        err instanceof Error ? err.message : "Failed to charge card on file",
      );
    } finally {
      setIsProcessing(false);
    }
  };

  /**
   * Redemption reduces the covered service line's remaining cost and draws down
   * the prepaid balance — it creates no payment row. Refetch the transaction so
   * the net per-line prices and updated status land, then surface success like
   * the card-on-file path.
   */
  const handleRedeemPackageSuccess = async (
    _response: RedeemPackageResponse,
  ) => {
    setError(null);
    try {
      if (onRefetchTransaction) {
        await onRefetchTransaction();
      } else {
        const refreshed = await getTransaction(transaction.transaction.id);
        if (refreshed) {
          onTransactionUpdated(refreshed);
        }
      }
    } catch (err) {
      console.error("Error refreshing transaction after redemption:", err);
    }
    // Refresh balances so the reveal reflects the drawn-down package.
    await checkForPackages();
    settleWithSuccess();
  };

  /**
   * Loyalty redemption already settled server-side (method='Loyalty' tender);
   * refetch so the payments list and balance reflect it — the same path the
   * card-on-file charge uses. A redemption covering the full balance settles
   * like other tenders; a partial one keeps staff here to charge the rest.
   */
  const handleLoyaltyRedeemed = async (
    response: RedeemLoyaltyRewardResponse,
  ) => {
    setError(null);
    try {
      if (onRefetchTransaction) {
        await onRefetchTransaction();
      } else {
        const refreshed = await getTransaction(transaction.transaction.id);
        if (refreshed) {
          onTransactionUpdated(refreshed);
        }
      }
    } catch (err) {
      console.error(
        "Error refreshing transaction after loyalty redemption:",
        err,
      );
    }
    const applied = Number(response.applied_value) || 0;
    if (applied >= remainingBalance - 0.004) {
      settleWithSuccess();
    }
  };

  // Only packages with some redeemable balance are shown (the backend already
  // excludes depleted/expired; this guards against a 0-balance row rendering
  // with a misleading "nothing eligible" note).
  const availablePackages = useMemo(
    () => clientPackages.filter(hasRemainingBalance),
    [clientPackages],
  );

  // Any available package may be selected (even one inapplicable to this ticket,
  // to show why); its covered lines drive the redeem controls and validity.
  const selectedPackage = availablePackages.find(
    (pkg) => pkg.id === state.selectedPackageId,
  );
  const coveredLines = useMemo(
    () =>
      buildCoveredLines(
        selectedPackage,
        transaction?.line_items?.booked_services ?? [],
      ),
    [selectedPackage, transaction?.line_items?.booked_services],
  );

  // Covered lines carrying a non-zero redeem amount, each capped by the
  // shared-balance-aware max (two ticket lines of one service share a balance).
  const redemptionItems = coveredLines
    .map((line) => ({
      line,
      qty: Number(state.packageRedemptions[line.bookedServiceId]) || 0,
    }))
    .filter((entry) => entry.qty > 0);

  const allLinesWithinCap = redemptionItems.every(
    (entry) =>
      entry.qty <=
      sharedBalanceAwareMax(
        entry.line,
        state.packageRedemptions,
        coveredLines,
      ) +
        1e-9,
  );
  // Non-zero guard: at least one line must redeem a positive amount.
  const packageSelectionValid =
    !!selectedPackage && redemptionItems.length > 0 && allLinesWithinCap;

  // True when a package is chosen and covers on-ticket services, but every one
  // is already fully redeemed (all caps are 0) — nothing left to enter.
  const allCapsZero =
    coveredLines.length > 0 &&
    coveredLines.every(
      (line) => sharedBalanceAwareMax(line, {}, coveredLines) === 0,
    );

  // Tooltip explaining a blocked Redeem button for the Package method — each
  // reachable blocked state gets its own reason.
  const packageDisabledReason =
    state.payMethod === "Package" && !packageSelectionValid
      ? !selectedPackage
        ? "Select a package to redeem from."
        : coveredLines.length === 0
          ? "Nothing on this ticket is eligible for this package."
          : allCapsZero
            ? "Everything eligible on this ticket has already been redeemed."
            : "Enter an amount to redeem from this package."
      : undefined;

  const handleRedeemPackage = async () => {
    if (!packageSelectionValid || !selectedPackage) return;
    setIsProcessing(true);
    setError(null);
    try {
      const response = await redeemPackage(salonId, {
        transaction_id: transaction.transaction.id,
        client_service_package_id: selectedPackage.id,
        redemptions: redemptionItems.map((entry) => ({
          salon_service_id: entry.line.salonServiceId,
          quantity: entry.qty,
          booked_service_id: entry.line.bookedServiceId,
        })),
      });
      await handleRedeemPackageSuccess(response);
    } catch (err) {
      console.error("Failed to redeem package:", err);
      setError(
        err instanceof Error ? err.message : "Failed to redeem package.",
      );
    } finally {
      setIsProcessing(false);
    }
  };

  const handleRedeemGiftCardSuccess = async () => {
    setError(null);
    try {
      if (onRefetchTransaction) {
        await onRefetchTransaction();
      } else {
        const refreshed = await getTransaction(transaction.transaction.id);
        if (refreshed) {
          onTransactionUpdated(refreshed);
        }
      }
    } catch (err) {
      console.error(
        "Error refreshing transaction after gift card redemption:",
        err,
      );
    }
    // Refresh the balance so the reveal reflects the drawn-down card(s).
    await checkForGiftCards();
    settleWithSuccess();
  };

  const handleRedeemGiftCard = async () => {
    // A gift card tender needs a client and some spendable balance.
    if (giftCardAmountToApply <= 0 || !transaction?.client?.id) return;
    setIsProcessing(true);
    setError(null);
    try {
      await redeemGiftCard(salonId, {
        transaction_id: transaction.transaction.id,
        client_id: transaction.client.id,
        amount: giftCardAmountToApply,
      });
      await handleRedeemGiftCardSuccess();
    } catch (err) {
      console.error("Failed to redeem gift card:", err);
      setError(
        err instanceof Error ? err.message : "Failed to redeem gift card.",
      );
    } finally {
      setIsProcessing(false);
    }
  };

  /** Dispatch to the flow for the currently selected payment method. */
  const proceedWithSelectedMethod = () => {
    if (state.payMethod === "Card") {
      handleTerminalPayment();
    } else if (isBnplSelection(state.payMethod)) {
      handleTerminalPayment(bnplMethodForSelection(state.payMethod));
    } else if (state.payMethod === "CardOnFile") {
      handleChargeCardOnFile();
    } else if (state.payMethod === "Cash") {
      setError(null);
      // The charge amount doubles as the cash received. Over the balance:
      // ask how the overage splits between tip and change; otherwise the
      // payment records as-is.
      if (chargeAmount > remainingBalance + 0.004) {
        setShowCashOverageModal(true);
      } else {
        handleCashCharge(0);
      }
    } else if (state.payMethod === "Package") {
      handleRedeemPackage();
    } else if (state.payMethod === "GiftCard") {
      handleRedeemGiftCard();
    } else {
      handleNonCardPayment();
    }
  };

  /**
   * Commit the staged loyalty picks (from Appointment Details) as one
   * flat-rate `{points}` POST. Returns true when Charge may proceed
   * (nothing staged, already committed, or the redeem succeeded); false
   * when the redeem failed and Charge must abort. Staged picks are
   * preserved on failure so staff can retry; cleared on success.
   */
  const commitStagedRewards = useCallback(async (): Promise<boolean> => {
    if (loyaltyAlreadyRedeemed) return true;
    const picks = stagedRewards ?? [];
    if (picks.length === 0 || stagedLoyaltyPoints <= 0) return true;
    setError(null);
    try {
      const response = await redeemLoyaltyReward(transaction.transaction.id, {
        points: stagedLoyaltyPoints,
      });
      // Refresh so the balance the payment method spends is netted of the
      // new Loyalty tender.
      await handleLoyaltyRedeemed(response);
      onStagedRewardsChange?.([]);
      return true;
    } catch (err: any) {
      const bodyMessage =
        err?.errorBody && typeof err.errorBody === "object"
          ? err.errorBody.message || err.errorBody.error
          : null;
      setError(
        err?.status >= 400 &&
          err?.status < 500 &&
          typeof bodyMessage === "string" &&
          bodyMessage.trim()
          ? bodyMessage
          : "Could not apply loyalty reward. Please try again.",
      );
      return false;
    }
    // handleLoyaltyRedeemed is stable enough (bound via closure over refetch
    // and setError); listing it here would churn the callback identity every
    // render without changing behavior. CRA base ESLint doesn't load
    // react-hooks/exhaustive-deps, so no disable comment is needed here.
  }, [
    loyaltyAlreadyRedeemed,
    stagedRewards,
    stagedLoyaltyPoints,
    transaction.transaction.id,
    redeemLoyaltyReward,
    onStagedRewardsChange,
  ]);

  // Per-unit/syringe quantities are set on the details screen's steppers, so
  // charging proceeds straight to the selected payment flow — no re-confirm.
  // Any staged loyalty picks commit first as a single flat-rate `{points}`
  // POST; a false return aborts Charge and preserves the picks.
  const handleChargeClick = async () => {
    const ok = await commitStagedRewards();
    if (!ok) return;
    proceedWithSelectedMethod();
  };

  /** Records a cash payment: `chargeAmount` is the cash received. */
  const handleCashCharge = (tipFromOverage: number) => {
    const tendered = Math.round(chargeAmount * 100) / 100;
    const billPortion = Math.min(tendered, remainingBalance);
    const tip = Math.round(tipFromOverage * 100) / 100;
    const change = Math.max(
      0,
      Math.round((tendered - billPortion - tip) * 100) / 100,
    );
    setShowCashOverageModal(false);
    handleNonCardPayment({
      amount: billPortion,
      tendered,
      change,
      tip_amount: tip,
    });
  };

  const handleTipSubmit = async (tipData: EditTransactionLineItemsRequest) => {
    try {
      const bsTipsByIds =
        tipData.line_items.booked_services?.reduce(
          (acc, service) => {
            acc[service.id] = service.tip || 0;
            return acc;
          },
          {} as Record<string, number>,
        ) || {};

      const finalTipData: EditTransactionLineItemsRequest = {
        transaction_id: transaction.transaction.id,
        line_items: {
          booked_services: transaction.line_items.booked_services.map(
            (service) => ({
              id: service.id,
              line_item_id: service.line_item_id,
              tip: bsTipsByIds[service.id] || 0,
            }),
          ),
        },
      };

      const response = await editTransactionLineItems(finalTipData);
      if (response && response.line_items) {
        const updatedTransaction = {
          ...transaction,
          line_items: response.line_items,
        };
        onTransactionUpdated(updatedTransaction);
      }

      setShowTipModal(false);
    } catch (err) {
      console.error("Failed to update tips:", err);
      throw err;
    }
  };

  // ── Charge button state ─────────────────────────────────────────

  const selectedCard = (clientPaymentMethods ?? []).find(
    (card) => card.id === state.selectedCardId,
  );

  let chargeDisabled = !state.payMethod || isProcessing;
  if (state.payMethod === "CardOnFile") {
    chargeDisabled =
      chargeDisabled || !selectedCard || isCardExpired(selectedCard);
  }
  // Card methods need a positive amount to capture — a tip-only charge on a
  // $0-service ticket is fine (the tip lifts chargeAmount), but a genuine $0
  // (nothing entered) would be rejected by Stripe, so keep it disabled.
  if (state.payMethod === "Card" || state.payMethod === "CardOnFile") {
    chargeDisabled = chargeDisabled || chargeAmount <= 0;
  }
  if (state.payMethod === "Package") {
    chargeDisabled = chargeDisabled || !packageSelectionValid;
  }
  if (state.payMethod === "GiftCard") {
    chargeDisabled = chargeDisabled || giftCardAmountToApply <= 0;
  }
  if (isBnplSelection(state.payMethod)) {
    const method = bnplMethodForSelection(state.payMethod);
    const inRange =
      !!method &&
      chargeAmount >= method.minAmount &&
      chargeAmount <= method.maxAmount;
    chargeDisabled = chargeDisabled || !inRange;
  }

  const chargeLabel =
    state.payMethod === "Package"
      ? "Redeem Package"
      : state.payMethod === "GiftCard"
        ? `Redeem ${formatCurrency(giftCardAmountToApply)}`
        : `Charge ${formatCurrency(chargeAmount)}`;

  // Override applies to every money method; for Cash it doubles as the
  // cash-received amount. Package redemption stays quantity-based, and the
  // gift card tender always applies the capped min — neither takes an override.
  const showDifferentAmount =
    state.payMethod !== "Package" && state.payMethod !== "GiftCard";

  // A transaction is locked from further checkout only when it is BOTH
  // completed AND paid. A $0 remaining balance alone must NOT lock it —
  // packages stay redeemable (redemption is quantity-based) and tips can still
  // be added on $0/low-dollar tickets. "Paid" uses the backend
  // transaction_status flag, not the derived balance we're intentionally
  // no longer gating on.
  const isClosedOut =
    isTransactionInFinalState(transaction) &&
    transaction.transaction.transaction_status === "paid";

  // On a zero-service ticket the terminal reader can't compute a percentage
  // tip (its eligible base is $0), so the tip is typed in-app instead. Enable
  // the in-app "Add tip" for the Card method only in that case; non-zero Card
  // tickets keep collecting the tip on the reader. `undiscountedServiceTotal`
  // is the same sum-of-`calc__cost_excl_tips_discounts` the backend uses for
  // the reader's tip-eligible base, so the two agree on "zero ticket".
  const zeroServiceTicket = totals.undiscountedServiceTotal <= 0;
  const canEditTipsInApp =
    (state.payMethod !== "Card" || zeroServiceTicket) && !packageSaleName;

  // ── Render ──────────────────────────────────────────────────────

  const rightColumn = showSuccess ? (
    <CheckoutSummaryPanel
      transaction={transaction}
      totals={totals}
      payMethod={state.payMethod}
      chargeAmount={chargeAmount}
      overrideAmount={null}
      packageSaleName={packageSaleName}
      amountDue={amountDue}
      amountNote={amountNote}
      salonServices={salonServices}
      stagedLoyaltyDollars={stagedLoyaltyDollars}
    />
  ) : (
    <>
      <CheckoutSummaryPanel
        transaction={transaction}
        totals={totals}
        payMethod={state.payMethod}
        chargeAmount={chargeAmount}
        overrideAmount={state.overrideAmount}
        onEditTips={canEditTipsInApp ? () => setShowTipModal(true) : undefined}
        packageSaleName={packageSaleName}
        amountDue={amountDue}
        amountNote={amountNote}
        salonServices={salonServices}
        stagedLoyaltyDollars={stagedLoyaltyDollars}
        stagedRewards={stagedRewards}
      />

      {/* Salon-configured checkout reminder, repeated from the transaction
          modal so staff see it right before charging. */}
      <ConnectedCheckoutReminderAlert />

      <Box className={surfaceStyles.actionStack}>
        <Tooltip
          label={packageDisabledReason}
          multiline
          w={260}
          withArrow
          disabled={!packageDisabledReason}
        >
          {/* Box wrapper keeps hover working over the disabled button. */}
          <Box>
            <Button
              fullWidth
              classNames={{ root: surfaceStyles.primaryAction }}
              leftSection={<IconLock size={16} />}
              onClick={handleChargeClick}
              disabled={chargeDisabled || isClosedOut}
              loading={isProcessing}
            >
              {chargeLabel}
            </Button>
          </Box>
        </Tooltip>

        {showDifferentAmount && (
          <Button
            fullWidth
            classNames={{ root: styles.differentAmount }}
            leftSection={<IconPencil size={15} />}
            onClick={() => setShowAmountModal(true)}
            disabled={isProcessing || isClosedOut}
          >
            Charge a different amount
          </Button>
        )}

        <Button
          fullWidth
          classNames={{ root: styles.cancelCheckout }}
          leftSection={<IconX size={15} />}
          onClick={onCancelCheckout}
          disabled={isProcessing}
        >
          Cancel Checkout
        </Button>
      </Box>
    </>
  );

  const clientName = `${transaction.client?.first_name ?? ""} ${
    transaction.client?.last_name ?? ""
  }`.trim();
  const checkoutTitle = clientName ? `Checkout for ${clientName}` : "Checkout";

  const backButton = (
    <Button
      classNames={{ root: styles.backButton }}
      leftSection={<IconChevronLeft size={17} />}
      onClick={onBack}
      disabled={isProcessing}
    >
      Back
    </Button>
  );

  const leftColumn = (
    <SurfacePaper>
      <Title order={2} className={styles.checkoutTitle}>
        {checkoutTitle}
      </Title>
      {error && (
        <Alert color="red" title="Error" mb="md">
          {error}
        </Alert>
      )}
      {showSuccess ? (
        <Stack gap="xl" p="sm">
          <PaymentSuccessMessage />
        </Stack>
      ) : (
        <>
          {amountDue !== undefined && (
            <Stack gap={2} mb="md">
              <Text size="xl" fw={500}>
                {formatCurrency(amountDue)}
              </Text>
              {amountNote && (
                <Text size="sm" c="dimmed">
                  {amountNote}
                </Text>
              )}
            </Stack>
          )}
          <PaymentMethodPanel
            businessInfo={businessInfo}
            state={state}
            actions={actions}
            isLoading={isLoadingMethods}
            cards={clientPaymentMethods}
            showCardOnFile={showCardOnFile}
            packages={availablePackages}
            salonServices={salonServices ?? []}
            bookedServices={transaction.line_items?.booked_services ?? []}
            offPlatformMethods={selectedOffPlatform}
            chargeAmount={chargeAmount}
            giftCardBalance={clientGiftCardBalance}
            giftCardAmountToApply={giftCardAmountToApply}
            clientId={transaction.client?.id}
            onAddCard={() => setShowAddCardModal(true)}
            onCardsChanged={checkForCardsOnFile}
          />
        </>
      )}
    </SurfacePaper>
  );

  return (
    <>
      <Box pos="relative">
        <BrandedLoadingOverlay visible={isProcessing} />
        <FullscreenShell
          topLeft={backButton}
          onClose={onCloseModal}
          left={leftColumn}
          right={rightColumn}
        />
      </Box>

      <PaymentProcessingModal
        opened={showPaymentModal}
        cardOnFile={paymentProcessingCardOnFile}
        paymentMethod={terminalPaymentMethod}
        onClose={(isSuccess?: boolean) => {
          setShowPaymentModal(false);
          setPaymentProcessingCardOnFile(false);
          setTerminalPaymentMethod("card");
          if (isSuccess) {
            // Show the "Checkout Complete" state (with the same 3s auto-close
            // as the cash / redemption paths) instead of dropping straight
            // back to the method view.
            settleWithSuccess();
          }
        }}
        transactionId={transaction.transaction.id}
        paymentIntentId={paymentIntentId}
        eligibleAmountForTip={eligibleAmountForTip}
        terminalDeviceId={state.selectedTerminalDeviceId}
        customerEmail={transaction.client?.email}
        transaction={transaction}
        onTransactionUpdated={(
          updatedTransaction: ProcessCardPaymentResponse,
        ) =>
          onTransactionUpdated(
            mapUpdatedTransactionAndPayments(updatedTransaction),
          )
        }
        onRefetchTransaction={onRefetchTransaction}
      />

      <CashOverageModal
        opened={showCashOverageModal}
        onClose={() => {
          if (!isProcessing) setShowCashOverageModal(false);
        }}
        tendered={chargeAmount}
        balanceDue={remainingBalance}
        onConfirm={handleCashCharge}
      />

      {transaction.client?.id && salonId && (
        <AddCardModal
          opened={showAddCardModal}
          onClose={() => setShowAddCardModal(false)}
          clientId={transaction.client.id}
          salonId={salonId}
          stripeAccountId={businessInfo?.stripe_connect_account_id}
          clientName={`${transaction.client.first_name ?? ""} ${
            transaction.client.last_name ?? ""
          }`.trim()}
          onSuccess={() => {
            setShowAddCardModal(false);
            checkForCardsOnFile();
          }}
        />
      )}

      <TipModal
        opened={showTipModal}
        onClose={() => setShowTipModal(false)}
        transaction={transaction}
        onSubmit={handleTipSubmit}
      />

      <ChargeAmountModal
        opened={showAmountModal}
        onClose={() => setShowAmountModal(false)}
        fullAmount={remainingBalance}
        overrideAmount={state.overrideAmount}
        onApply={actions.setOverrideAmount}
        allowExceed={state.payMethod === "Cash"}
        description={
          state.payMethod === "Cash"
            ? "Enter the cash received from the customer. Anything above the balance due can be kept as tip or returned as change."
            : undefined
        }
      />
    </>
  );
}
